import React, { useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RotationData {
  pool_used: boolean;
  pool_size?: number;
  selection_method?: string;
}

interface ModelColumnCellProps {
  model_used?: string | null;
  provider?: string | null;
  rotation_data?: RotationData;
  model_override?: string | null;
  compact?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Strip the provider prefix and return just the model name portion. */
function cleanModelName(rawId: string): string {
  const parts = rawId.split('/');
  return parts.length > 1 ? parts.slice(1).join('/') : rawId;
}

function normalizeValue(value?: string | null): string | null {
  const normalized = value?.trim();
  return normalized || null;
}

function formatProvider(provider: string): string {
  const knownProviders: Record<string, string> = {
    anthropic: 'Anthropic',
    fireworks: 'Fireworks',
    openai: 'OpenAI',
    openrouter: 'OpenRouter',
  };
  return knownProviders[provider.toLowerCase()] || provider;
}

/** Max characters before we truncate with ellipsis. */
const MAX_DISPLAY_LENGTH = 32;

function truncate(name: string): { display: string; isTruncated: boolean } {
  if (name.length <= MAX_DISPLAY_LENGTH) {
    return { display: name, isTruncated: false };
  }
  return {
    display: name.slice(0, MAX_DISPLAY_LENGTH - 3) + '...',
    isTruncated: true,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ModelColumnCell: React.FC<ModelColumnCellProps> = ({
  model_used,
  provider: _provider,
  rotation_data,
  model_override,
  compact = false,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const normalizedModel = normalizeValue(model_used);
  const normalizedProvider = normalizeValue(_provider);

  if (!normalizedModel && !normalizedProvider) {
    return (
      <span className="text-xs text-muted-foreground select-none">Unknown</span>
    );
  }

  const cleaned = normalizedModel ? cleanModelName(normalizedModel) : null;
  const { display, isTruncated } = cleaned
    ? truncate(cleaned)
    : { display: 'Unknown model', isTruncated: false };

  const showRotationBadge = rotation_data?.pool_used === true;
  const showOverrideBadge = !!model_override;
  const providerLabel = normalizedProvider
    ? formatProvider(normalizedProvider)
    : 'Provider unknown';

  if (compact) {
    return (
      <span
        className="inline-block min-w-0 max-w-full truncate text-[11px] text-muted-foreground"
        title={`${cleaned || 'Unknown model'} / ${providerLabel}`}
      >
        {cleaned || 'Unknown model'} / {providerLabel}
      </span>
    );
  }

  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <div className="flex min-w-0 flex-col leading-tight">
        <span
          className="truncate text-sm text-foreground relative"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {isTruncated ? (
            <>
              {display}
              {showTooltip && normalizedModel && (
                <span
                  role="tooltip"
                  className="absolute left-0 bottom-full mb-1 z-50 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white shadow-lg"
                >
                  {normalizedModel}
                </span>
              )}
            </>
          ) : (
            display
          )}
        </span>
        <span className="truncate text-[11px] text-muted-foreground">
          {providerLabel}
        </span>
      </div>

      {showRotationBadge && (
        <span
          data-testid="rotation-badge"
          className="inline-flex items-center rounded-full bg-teal-light px-1.5 py-0.5 text-[10px] font-medium text-teal-dark shrink-0"
        >
          Rotation
        </span>
      )}

      {showOverrideBadge && (
        <span
          data-testid="override-badge"
          className="inline-flex items-center rounded-full bg-coral-light px-1.5 py-0.5 text-[10px] font-medium text-coral-dark shrink-0"
        >
          Override
        </span>
      )}
    </div>
  );
};
